/**
 * Manage members of a group. Live list of current members + an
 * "Add user" picker on top. Backend is the source of truth — every
 * add/remove refetches the group detail.
 *
 * Safety: the backend refuses to let an admin remove themselves from
 * the "admin" group, so the UI doesn't need to special-case it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserMinus, UserPlus, X } from "lucide-react";
import * as React from "react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { usersApi, type GroupSummary } from "@/lib/api-users";

export interface GroupMembersDialogProps {
  group: GroupSummary | null;
  onOpenChange: (open: boolean) => void;
}

export function GroupMembersDialog({ group, onOpenChange }: GroupMembersDialogProps) {
  const qc = useQueryClient();
  const open = !!group;

  const groupDetailQuery = useQuery({
    queryKey: ["group", group?.group_id],
    queryFn: () =>
      group ? usersApi.getGroup(group.group_id) : Promise.reject("no group"),
    enabled: open,
  });

  const allUsersQuery = useQuery({
    queryKey: ["users", "all-for-picker"],
    queryFn: () => usersApi.list({ page_size: 100 }),
    enabled: open,
    select: (page) => page.items,
  });

  const addMutation = useMutation({
    mutationFn: (userId: string) =>
      usersApi.addGroupMember(group!.group_id, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group", group?.group_id] });
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) =>
      usersApi.removeGroupMember(group!.group_id, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group", group?.group_id] });
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const [pickUser, setPickUser] = React.useState<string>("");

  const memberIds = new Set(
    (groupDetailQuery.data?.members ?? []).map((m) => m.user_id),
  );
  const eligibleUsers =
    (allUsersQuery.data ?? []).filter((u) => !memberIds.has(u.user_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              Members of {group?.name ?? group?.group_id}
            </DialogTitle>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {/* Add user */}
          <div className="space-y-1.5">
            <Label htmlFor="add-member">Add user</Label>
            <div className="flex items-center gap-2">
              <select
                id="add-member"
                className="flex-1 border border-border bg-background px-2 py-1.5 text-xs"
                value={pickUser}
                onChange={(e) => setPickUser(e.target.value)}
              >
                <option value="">— pick a user —</option>
                {eligibleUsers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.username} ({u.email})
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={!pickUser || addMutation.isPending}
                onClick={() => {
                  if (pickUser) {
                    addMutation.mutate(pickUser);
                    setPickUser("");
                  }
                }}
              >
                {addMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" strokeWidth={1.5} />
                )}
                Add
              </Button>
            </div>
          </div>

          {/* Members list */}
          <div className="space-y-1.5">
            <Label>Current members</Label>
            {!groupDetailQuery.data ? (
              <div className="text-xs text-muted-foreground">Loading…</div>
            ) : groupDetailQuery.data.members.length === 0 ? (
              <div className="border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
                No members.
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden border border-border/60">
                {groupDetailQuery.data.members.map((m) => (
                  <div
                    key={m.user_id}
                    className="flex items-center justify-between border-b border-border px-2 py-1.5 text-xs last:border-b-0"
                  >
                    <div>
                      <div className="font-medium">{m.username}</div>
                      <div className="text-xs text-muted-foreground">
                        {m.email}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={removeMutation.isPending}
                      onClick={() => removeMutation.mutate(m.user_id)}
                      title="Remove from group"
                    >
                      <UserMinus
                        className="h-3.5 w-3.5 text-destructive"
                        strokeWidth={1.5}
                      />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {addMutation.isError && (
            <Alert tone="danger">
              {(addMutation.error as Error)?.message ?? "Add failed"}
            </Alert>
          )}
          {removeMutation.isError && (
            <Alert tone="danger">
              {(removeMutation.error as Error)?.message ?? "Remove failed"}
            </Alert>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
